/**
 * 引擎档位名(OEM 贴牌 0929,C-C)—— 对客一律说「档位」,不说厂商 / 模型真名。
 *
 * ═══ 口径(设计-console.md §2.3,老板拍板:直营和 OEM 对客都改档位名)═════════════
 *   出图 provider            真人出镜 model
 *   DOUBAO          极速      vapeur-seedance-2-5       旗舰
 *   WANXIANG        精绘      vapeur-seedance-2-0       专业
 *   OPENAI          标准      vapeur-seedance-2-0-fast  极速
 *   OPENAI_VIP      高清      vapeur-seedance-2-0-mini  轻量
 *   OPENAI_FLARE    旗舰·极速
 *   OPENAI_SUNBURST 旗舰·精修
 *   GOOGLE          创意
 *
 * 视频(OEM H2,2026-10-01「按企业级标准产品来」):对客名 = {质量档}·{生成方式}
 *   (旗舰 > 高清 > 专业 > 标准 > 极速;综合 / 文生视频 / 图生视频 / 参考生视频)。
 *   ★视频档位名的单一真源在 api `VideoModelTiers`,经 `/video/provider-capabilities` 的
 *   label 与任务 DTO 的 modelLabel 下发;{@link VIDEO_MODEL_TIERS} 只是拿不到后端名时的离线镜像
 *   (历史任务 / 旧接口),改名先改 api 再同步这里。
 *
 * 平台超管在非代客态下(vendorMask 的 reveal 开关),档位名后附真名「标准(GPT Image)」,
 * 方便排障;代客态和租户看到的一模一样。
 *
 * ★本文件是 provider/model → 对客名的**唯一来源**;业务里别再写「豆包」「GPT」这种字面量。
 *   真名表({@link ENGINE_REAL_NAMES})只在 reveal 时拼接,不单独对客展示。
 */
import { isVendorRevealed } from "@/shared/lib/vendorMask";

export type EngineKind = "image" | "video" | "realPerson";

/** 出图 provider → 档位名。 */
export const IMAGE_ENGINE_TIERS: Readonly<Record<string, string>> = {
  DOUBAO: "极速",
  WANXIANG: "精绘",
  OPENAI: "标准",
  OPENAI_VIP: "高清",
  OPENAI_FLARE: "旗舰·极速",
  OPENAI_SUNBURST: "旗舰·精修",
  GOOGLE: "创意",
  // 上传素材任务不是出图引擎,但走同一张 provider 表(卡面/详情要说实话)。
  UPLOAD: "上传素材",
};

/**
 * 视频 provider → 系列名(只在**拿不到 model** 时兜底;有 model 一律按 {@link VIDEO_MODEL_TIERS})。
 * seedance 系三档(旗舰 / 专业 / 极速)都是「综合」,故系列名取生成方式而非质量档。
 */
export const VIDEO_ENGINE_TIERS: Readonly<Record<string, string>> = {
  seedance: "综合",
  "wan2.7": "标准",
  wan: "标准",
  happyhorse: "高清",
};

/** 视频 model_code → 对客档位名(api `VideoModelTiers` 的离线镜像,逐字一致)。 */
export const VIDEO_MODEL_TIERS: Readonly<Record<string, string>> = {
  "doubao-seedance-2-5-260628": "旗舰·综合",
  "doubao-seedance-2-0-260128": "专业·综合",
  "happyhorse-1.0-t2v": "高清·文生视频",
  "happyhorse-1.0-i2v": "高清·图生视频",
  "happyhorse-1.0-r2v": "高清·参考生视频",
  "wan2.7-t2v-2026-04-25": "标准·文生视频",
  "wan2.7-i2v-2026-04-25": "标准·图生视频",
  "doubao-seedance-2-0-fast-260128": "极速·综合",
};

/** 运行时从后端能力表学到的 model → label(后端为准,覆盖离线镜像)。 */
const runtimeVideoLabels = new Map<string, string>();

/** 能力表一到就登记后端下发的档位名(videoService.getCapabilities 调)。 */
export function rememberVideoModelLabels(
  rows: ReadonlyArray<{ model: string; label?: string | null; tier?: string | null }>
): void {
  for (const r of rows) {
    const label = r.label?.trim();
    // 只认带 tier 的新后端:老后端 label 是厂商真名,登记进来反而把名字漏给用户。
    if (r.model && label && r.tier) runtimeVideoLabels.set(r.model, label);
  }
}

/** 测试用:清空运行时登记。 */
export function resetVideoModelLabelsForTest(): void {
  runtimeVideoLabels.clear();
}

/** 视频 model_code → 档位名;未登记 → null(调用方自己决定兜底)。 */
export function videoModelTierLabel(model: string | null | undefined): string | null {
  if (!model) return null;
  return runtimeVideoLabels.get(model) ?? VIDEO_MODEL_TIERS[model] ?? null;
}

/** 真人出镜内部模型 id → 档位名。 */
export const REAL_PERSON_ENGINE_TIERS: Readonly<Record<string, string>> = {
  "vapeur-seedance-2-0": "专业",
  "vapeur-seedance-2-5": "旗舰",
  "vapeur-seedance-2-0-fast": "极速",
  "vapeur-seedance-2-0-mini": "轻量",
};

/** 真名(仅超管非代客态附在档位名后)。 */
export const ENGINE_REAL_NAMES: Readonly<Record<EngineKind, Readonly<Record<string, string>>>> = {
  image: {
    DOUBAO: "豆包 SeaDream",
    WANXIANG: "通义万相",
    OPENAI: "GPT Image",
    OPENAI_VIP: "GPT Image VIP",
    OPENAI_FLARE: "GPT 2.5 Flare",
    OPENAI_SUNBURST: "GPT 2.5 Sunburst",
    GOOGLE: "Gemini Image",
  },
  video: {
    seedance: "豆包 Seedance",
    "wan2.7": "万相 2.7",
    wan: "万相",
    happyhorse: "HappyHorse",
  },
  realPerson: {
    "vapeur-seedance-2-0": "Seedance 2.0",
    "vapeur-seedance-2-5": "Seedance 2.5",
    "vapeur-seedance-2-0-fast": "Seedance 2.0 fast",
    "vapeur-seedance-2-0-mini": "Seedance 2.0 mini",
  },
};

const FALLBACK: Readonly<Record<EngineKind, string>> = {
  image: "AI 出图",
  video: "AI 视频",
  realPerson: "专业",
};

/** 视频:有 model 按 model 取档位名;没有 / 未登记的 model 退回 provider 系列名。 */
function videoTier(provider: string, model: string | null | undefined): string | null {
  return videoModelTierLabel(model) ?? VIDEO_ENGINE_TIERS[provider] ?? null;
}

/** 不看 reveal 开关的纯档位名(守门单测、需要强制对客口径的地方用)。 */
export function engineTierName(
  kind: EngineKind,
  provider: string | null | undefined,
  model?: string | null
): string {
  if (kind === "realPerson") {
    const id = model ?? provider ?? "";
    return REAL_PERSON_ENGINE_TIERS[id] ?? FALLBACK.realPerson;
  }
  if (kind === "video") return videoTier(provider ?? "", model) ?? FALLBACK.video;
  if (!provider) return FALLBACK[kind];
  return IMAGE_ENGINE_TIERS[provider] ?? FALLBACK.image;
}

function realName(
  kind: EngineKind,
  provider: string | null | undefined,
  model?: string | null
): string | null {
  if (kind === "realPerson") {
    const id = model ?? provider ?? "";
    return ENGINE_REAL_NAMES.realPerson[id] ?? (id || null);
  }
  if (!provider) return kind === "video" && model ? model : null;
  const base = ENGINE_REAL_NAMES[kind][provider] ?? provider;
  // 视频带 model 时把 model id 一起亮出来(超管排障要的正是它)。
  return kind === "video" && model ? `${base} · ${model}` : base;
}

/**
 * 对客引擎名。普通用户 / 代客态 = 档位名;平台超管非代客态 = 「档位名(真名)」。
 *
 *     engineLabel("image", "OPENAI_FLARE")                        // 旗舰·极速
 *     engineLabel("video", "seedance", "doubao-seedance-2-0-fast-260128") // 极速·综合
 *     engineLabel("realPerson", null, "vapeur-seedance-2-5")      // 旗舰
 */
export function engineLabel(
  kind: EngineKind,
  provider: string | null | undefined,
  model?: string | null
): string {
  const tier = engineTierName(kind, provider, model);
  if (!isVendorRevealed()) return tier;
  const real = realName(kind, provider, model);
  if (!real || real === tier || (kind === "image" && provider === "UPLOAD")) return tier;
  return `${tier}(${real})`;
}

/**
 * 视频对客名,优先用后端直出的档位名(能力表 label / 任务 modelLabel),没有再按 model 查镜像表。
 * 超管非代客态同 {@link engineLabel}:「档位名(真名 · model id)」双显。
 */
export function videoEngineLabel(
  provider: string | null | undefined,
  model?: string | null,
  serverLabel?: string | null
): string {
  const tier = serverLabel?.trim() || engineTierName("video", provider, model);
  if (!isVendorRevealed()) return tier;
  const real = realName("video", provider, model);
  return real && real !== tier ? `${tier}(${real})` : tier;
}
